import React, { useEffect } from "react";
import { AlertTriangle, ArrowLeft, Repeat, BoxIcon } from "lucide-react";
import { focusRing } from "../../utils/demoUi";
export type ShellSection = {
  id: string;
  label: string;
  icon: BoxIcon;
  count?: number;
};
type DemoShellProps = {
  domainId: string;
  productLabel: string;
  workspaceLabel: string;
  sections: ShellSection[];
  current: string;
  onSelect: (id: string) => void;
  onSwitchWorkspace: () => void;
  persistenceOk: boolean;
  /** Domain-specific notice, e.g. the synthetic research collection. */
  extraNotice?: string;
  /** Optional controls shown above the content, e.g. demonstration record access. */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
};
export const demoNotices = ['Interactive prototype · Fictional records · Saved in this browser.', 'Workspace switching demonstrates roles; it is not secure account access.'];
export function DemoShell({
  domainId,
  productLabel,
  workspaceLabel,
  sections,
  current,
  onSelect,
  onSwitchWorkspace,
  persistenceOk,
  extraNotice,
  toolbar,
  children
}: DemoShellProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [current]);
  const navButton = (section: ShellSection, compact: boolean) => {
    const Icon = section.icon;
    const active = section.id === current;
    return <button type="button" aria-current={active ? 'page' : undefined} onClick={() => onSelect(section.id)} className={`flex items-center gap-3 rounded-lg font-medium whitespace-nowrap transition-colors duration-150 ${focusRing} ${compact ? 'px-3 py-2 text-sm' : 'w-full px-3 py-2.5 text-left'} ${active ? 'bg-navy text-white' : 'text-ink-dark hover:bg-white'}`} data-id="element-444" data-mp="1krrhxalafdcf">
        <Icon size={compact ? 16 : 18} aria-hidden="true" className={active ? 'text-white' : 'text-lavender'} data-id="element-445" data-mp="1140i3m3ad1fk" />
        <span className={compact ? '' : 'flex-1'} data-id="element-446" data-mp="2hi0i0x24sr2t">{section.label}</span>
        {section.count !== undefined && section.count > 0 && <span className={`rounded-full px-2 text-xs font-semibold ${active ? 'bg-white/20 text-white' : 'bg-lavender-pale text-lavender'}`} data-id="element-447" data-mp="2ci5pkp2pjtfn">
            {section.count}
          </span>}
      </button>;
  };
  return <div className="min-h-screen bg-[#F3F5FA] text-ink-dark" data-id="element-448" data-mp="1f3jcxxh7ma8c">
      <header className="sticky top-0 z-40 border-b border-line/80 bg-white/85 backdrop-blur-md" data-id="element-449" data-mp="1j6pj754bo1et">
        <div className="flex items-center justify-between gap-3 px-4 py-3 lg:px-6" data-id="element-450" data-mp="1o5ahgz00kb0m">
          <div className="flex min-w-0 items-center gap-3" data-id="element-451" data-mp="rf9xreei4kq6">
            <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy font-heading text-sm font-bold text-white" data-id="element-452" data-mp="rguth7cjy9pm">
              N
            </span>
            <div className="min-w-0" data-id="element-453" data-mp="10eev4hebu80r">
              <p className="truncate text-xs font-medium text-lavender" data-id="element-454" data-mp="156ofzj0l4hw6">{productLabel}</p>
              <p className="truncate font-heading font-bold text-ink-dark" data-id="element-455" data-mp="158j3n538qo3y">{workspaceLabel}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-id="element-456" data-mp="cpvmg0rd7t85">
            <button type="button" onClick={onSwitchWorkspace} className={`inline-flex items-center gap-2 rounded-md border border-line bg-white px-3 py-2 text-sm font-semibold text-navy hover:bg-surface-muted transition-colors duration-150 ${focusRing}`} data-id="element-457" data-mp="coarwb51waaa">
              <Repeat size={16} aria-hidden="true" data-id="element-458" data-mp="3rd1eegm7fq0o" />
              <span className="hidden sm:inline" data-id="element-459" data-mp="3rfxf2cpfssst">Switch workspace</span>
              <span className="sr-only sm:hidden" data-id="element-460" data-mp="3nby2g57a2e4q">Switch workspace</span>
            </button>
            <a href={`/noetivra/demo-center#${domainId}`} className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-ink-muted hover:text-navy transition-colors duration-150 ${focusRing}`} data-id="element-461" data-mp="3iez7jkfsi35h">
              <ArrowLeft size={16} aria-hidden="true" data-id="element-462" data-mp="38lifakvaww3x" />
              Demo Center
            </a>
          </div>
        </div>
        <div className="border-t border-line/70 bg-lavender-pale/80 px-4 py-1.5 text-xs text-ink-muted lg:px-6" data-id="element-463" data-mp="2zmn7j4clzdyi">
          <p className="flex flex-wrap gap-x-4 gap-y-0.5" data-id="element-464" data-mp="2unabbszukuj9">
            {demoNotices.map(notice => <span key={notice} data-id="element-465" data-mp="ykxg9qe5sg0k">{notice}</span>)}
          </p>
        </div>
        {!persistenceOk && <p role="status" className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 lg:px-6" data-id="element-466" data-mp="12m94pslaqv76">
            <AlertTriangle size={16} aria-hidden="true" data-id="element-467" data-mp="ou2tmkx03eel" />
            Browser storage is unavailable. Changes are available only for the current session.
          </p>}
        <nav aria-label={`${workspaceLabel} sections`} className="overflow-x-auto border-t border-line/70 lg:hidden" data-id="element-468" data-mp="ew6j9vbw0nhj">
          <ul className="flex gap-1 px-3 py-2" data-id="element-469" data-mp="jwu7k98qeq82">
            {sections.map(section => <li key={section.id} data-id="element-470" data-mp="5w9p5e6qawf3">{navButton(section, true)}</li>)}
          </ul>
        </nav>
      </header>

      <div className="flex" data-id="element-471" data-mp="aukzdq0xr8xu">
        <nav aria-label={`${workspaceLabel} sections`} className="sticky top-24 hidden h-[calc(100vh-6rem)] w-64 shrink-0 self-start overflow-y-auto border-r border-line/70 bg-white/50 px-3 py-6 backdrop-blur-md lg:block" data-id="element-472" data-mp="avw2dnmk4cnw">
          <ul className="space-y-1" data-id="element-473" data-mp="2571d1bw29pcq">
            {sections.map(section => <li key={section.id} data-id="element-474" data-mp="2fe3wxux833u0">{navButton(section, false)}</li>)}
          </ul>
        </nav>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10 lg:py-10" data-id="element-475" data-mp="2af0socp2h1n3">
          <div className="mx-auto max-w-6xl" data-id="element-476" data-mp="2o5w4nxuv3yuw">
            {extraNotice && <p className="mb-6 rounded-lg border border-lavender/25 bg-lavender-pale px-4 py-2.5 text-sm font-medium text-ink-dark" data-id="element-477" data-mp="2o7h15t0qlrve">
                {extraNotice}
              </p>}
            {toolbar}
            {children}
          </div>
        </main>
      </div>
    </div>;
}